import {
  type ClipboardEvent,
  type KeyboardEvent,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";
import { twMerge } from "tailwind-merge";

import { INPUT_CLASS_NAMES } from "./edit-company-modal.constants";

const TEMPLATE_VARIABLE_PATTERN = /(\{\{\s*(?:company_name|job_title)\s*\}\})/g;
const EXACT_TEMPLATE_VARIABLE_PATTERN =
  /^\{\{\s*(?:company_name|job_title)\s*\}\}$/;

export const CodeToken = ({ children }: { children: string }) => (
  <code className="rounded-[3px] bg-uw-gray-0 px-1.5 py-0.5 text-xs font-semibold text-uw-gray-8">
    {children}
  </code>
);

const escapeTemplateEditorHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const renderTemplateEditorHtml = (
  value: string,
  highlightVariables: boolean,
) => {
  if (!highlightVariables) {
    return escapeTemplateEditorHtml(value);
  }

  return value
    .split(TEMPLATE_VARIABLE_PATTERN)
    .map((part) => {
      const escapedPart = escapeTemplateEditorHtml(part);

      if (!EXACT_TEMPLATE_VARIABLE_PATTERN.test(part)) {
        return escapedPart;
      }

      return `<span class="rounded-[3px] bg-uw-purple-0 px-1 font-bold text-uw-purple-3">${escapedPart}</span>`;
    })
    .join("");
};

const getTemplateEditorText = (editor: HTMLDivElement) =>
  editor.textContent ?? "";

const getSelectionOffset = (editor: HTMLDivElement) => {
  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0) {
    return getTemplateEditorText(editor).length;
  }

  const range = selection.getRangeAt(0);

  if (!editor.contains(range.commonAncestorContainer)) {
    return getTemplateEditorText(editor).length;
  }

  const leadingRange = range.cloneRange();
  leadingRange.selectNodeContents(editor);
  leadingRange.setEnd(range.endContainer, range.endOffset);
  return leadingRange.toString().length;
};

const setSelectionOffset = (editor: HTMLDivElement, offset: number) => {
  const selection = window.getSelection();

  if (!selection) {
    return;
  }

  const range = document.createRange();
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  let remainingOffset = offset;
  let currentNode = walker.nextNode();

  while (currentNode) {
    const textLength = currentNode.textContent?.length ?? 0;

    if (remainingOffset <= textLength) {
      range.setStart(currentNode, remainingOffset);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }

    remainingOffset -= textLength;
    currentNode = walker.nextNode();
  }

  range.selectNodeContents(editor);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

const insertPlainTextAtSelection = (editor: HTMLDivElement, text: string) => {
  editor.focus();

  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0) {
    editor.appendChild(document.createTextNode(text));
    setSelectionOffset(editor, getTemplateEditorText(editor).length);
    return;
  }

  const range = selection.getRangeAt(0);

  if (!editor.contains(range.commonAncestorContainer)) {
    editor.appendChild(document.createTextNode(text));
    setSelectionOffset(editor, getTemplateEditorText(editor).length);
    return;
  }

  range.deleteContents();

  const textNode = document.createTextNode(text);
  range.insertNode(textNode);
  range.setStart(textNode, text.length);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

interface TemplateTextareaProps {
  ariaLabel: string;
  dataTestId: string;
  disabled: boolean;
  id: string;
  highlightVariables?: boolean;
  minRows: number;
  onBlur?: () => void;
  onChange: (value: string) => void;
  value: string;
}

export const TemplateTextarea = ({
  ariaLabel,
  dataTestId,
  disabled,
  highlightVariables = true,
  id,
  minRows,
  onBlur,
  onChange,
  value,
}: TemplateTextareaProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastRenderedValueRef = useRef<string | null>(null);

  const renderValue = useCallback(
    (nextValue: string, selectionOffset?: number) => {
      const editor = editorRef.current;

      if (!editor) {
        return;
      }

      editor.innerHTML = renderTemplateEditorHtml(
        nextValue,
        highlightVariables,
      );
      lastRenderedValueRef.current = nextValue;

      if (selectionOffset !== undefined && document.activeElement === editor) {
        setSelectionOffset(editor, selectionOffset);
      }
    },
    [highlightVariables],
  );

  const syncValueFromEditor = useCallback(() => {
    const editor = editorRef.current;

    if (!editor) {
      return;
    }

    const selectionOffset = getSelectionOffset(editor);
    const nextValue = getTemplateEditorText(editor);

    renderValue(nextValue, selectionOffset);
    onChange(nextValue);
  }, [onChange, renderValue]);

  useLayoutEffect(() => {
    if (value === lastRenderedValueRef.current) {
      return;
    }

    const editor = editorRef.current;
    const selectionOffset =
      editor && document.activeElement === editor
        ? getSelectionOffset(editor)
        : undefined;

    renderValue(value, selectionOffset);
  }, [renderValue, value]);

  const handleInput = useCallback(() => {
    syncValueFromEditor();
  }, [syncValueFromEditor]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter") {
        return;
      }

      const editor = editorRef.current;

      if (!editor) {
        return;
      }

      event.preventDefault();
      insertPlainTextAtSelection(editor, "\n");
      syncValueFromEditor();
    },
    [syncValueFromEditor],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>) => {
      const editor = editorRef.current;

      if (!editor) {
        return;
      }

      event.preventDefault();
      insertPlainTextAtSelection(
        editor,
        event.clipboardData.getData("text/plain"),
      );
      syncValueFromEditor();
    },
    [syncValueFromEditor],
  );

  return (
    <div
      aria-disabled={disabled}
      aria-label={ariaLabel}
      aria-multiline={true}
      className={twMerge([
        INPUT_CLASS_NAMES.input,
        "w-full whitespace-pre-wrap break-words border border-solid bg-white px-3 py-2 outline-none",
        "focus:border-uw-purple-3",
        disabled && "cursor-not-allowed bg-uw-gray-0 text-uw-gray-5",
      ])}
      contentEditable={!disabled}
      data-testid={dataTestId}
      id={id}
      onBlur={onBlur}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      ref={editorRef}
      role="textbox"
      spellCheck
      style={{ minHeight: minRows * 20 + 18 }}
      suppressContentEditableWarning
      tabIndex={disabled ? -1 : 0}
    />
  );
};
